import { formatDateTime } from '../../../../utils/dates';
import { PRACTICE_DETAIL_GROUPS } from './fieldDefinitions';
import { DetailGroupCard } from './DetailGroupCard';
import { CopyAddressAction } from './CopyAddressAction';
import type { DetailFieldGridProps } from './DetailFieldGrid';

interface InfoSectionProps extends Omit<
    DetailFieldGridProps,
    'fields' | 'fieldAccessories' | 'showRequiredMarkers'
> {
    /** Server timestamp off practice.risk - not part of the dirty-tracked
     * form, so it's passed down separately rather than living in `values`. */
    riskUpdatedAt?: string | null;
}

const INFO_GROUP_IDS = new Set(['practice', 'doctor', 'risk']);

// The Details tab's "Info" rail pane - the Practice, Doctor, and Risk cards.
// The surrounding page scrolls now (see PracticeDetail.tsx), so this only
// lays the cards out vertically - no scroll/height ownership of its own.
export function InfoSection({ riskUpdatedAt, ...props }: InfoSectionProps) {
    const groups = PRACTICE_DETAIL_GROUPS.filter((group) => INFO_GROUP_IDS.has(group.id));

    // CALL adornment for the Practice card's phone field (see D2Field's
    // `adornment` in the design source) - only while a value exists; an
    // empty phone field has nothing to dial.
    const phone = props.values.phone?.trim();
    const fieldAdornments = phone
        ? {
              phone: (
                  <a
                      href={`tel:${phone}`}
                      className="text-[10px] font-bold text-gray-400 hover:text-gray-900"
                  >
                      CALL
                  </a>
              )
          }
        : undefined;

    return (
        <div className="flex flex-col gap-3.5 shrink-0">
            {groups.map((group) => (
                <DetailGroupCard
                    key={group.id}
                    group={group}
                    {...props}
                    fieldAdornments={fieldAdornments}
                    action={
                        group.id === 'practice' ? (
                            <CopyAddressAction values={props.values} />
                        ) : group.id === 'risk' && riskUpdatedAt ? (
                            <span className="text-[11px] text-gray-400 whitespace-nowrap">
                                Last updated {formatDateTime(riskUpdatedAt)}
                            </span>
                        ) : undefined
                    }
                />
            ))}
        </div>
    );
}
